import React, { useMemo } from 'react';
import styled from 'styled-components';
import { toCssDeclarationName } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/css-declarations';
import {
  InlineInputCardNameWrapper,
  InlineInputCardWrapper,
  InputDescriptionWrapper,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { NumberUnitInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/number-unit-input';
import { ColorSelectInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/color-select-input';
import {
  parseTextShadow,
  TextShadow,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/parsers';
import { useOnChange } from '@/components/hooks/use-on-change';

export interface CssTextShadowInputProps {
  name: string;
  initialValue?: string;
  onChange?: (name: string, value: string) => void;
}

export function CssTextShadowInput({
  name,
  initialValue,
  onChange,
}: CssTextShadowInputProps) {
  const displayName = useMemo(() => toCssDeclarationName(name), [name]);
  const [isEnabled, setIsEnabled] = React.useState(initialValue !== `none`);

  const defaultValues = useMemo<TextShadow[]>(() => {
    if (!initialValue || initialValue === `none`) {
      return [
        {
          inset: false,
          offsetX: `1px`,
          offsetY: `1px`,
          blurRadius: `10px`,
          color: `rgba(0, 0, 0, 1)`,
        },
      ];
    }
    return parseTextShadow(initialValue);
  }, [initialValue]);

  const [offsetX, setOffsetX] = React.useState(
    defaultValues?.[0].offsetX || `1px`,
  );
  const [offsetY, setOffsetY] = React.useState(
    defaultValues?.[0].offsetY || `1px`,
  );
  const [blurRadius, setBlurRadius] = React.useState(
    defaultValues?.[0].blurRadius || `1px`,
  );
  const [color, setColor] = React.useState(
    defaultValues?.[0].color || `rgba(0, 0, 0, 1)`,
  );

  const boxShadowOutput = useMemo(
    () => (isEnabled ? `${offsetX} ${offsetY} ${blurRadius} ${color}` : `none`),
    [offsetX, offsetY, blurRadius, color, isEnabled],
  );

  useOnChange(() => onChange?.(name, boxShadowOutput), [boxShadowOutput]);

  return (
    <InlineInputCardWrapper>
      <InlineInputCardNameWrapper>
        <CircularCheckmark
          square
          selected={isEnabled}
          onChange={setIsEnabled}
        />
        {` `}
        {displayName}
      </InlineInputCardNameWrapper>
      <ControlsWrapper className={isEnabled ? `` : `disabled`}>
        <InputDescriptionWrapper className="offset">
          <NumberUnitInput initialValue={offsetX} onChange={setOffsetX} />
          <span>X</span>
        </InputDescriptionWrapper>
        <InputDescriptionWrapper className="offset">
          <NumberUnitInput initialValue={offsetY} onChange={setOffsetY} />
          <span>Y</span>
        </InputDescriptionWrapper>

        <InputDescriptionWrapper className="offset">
          <NumberUnitInput initialValue={blurRadius} onChange={setBlurRadius} />
          <span>Blur</span>
        </InputDescriptionWrapper>
        <InputDescriptionWrapper>
          <ColorSelectInput
            initialValue={color}
            onChange={setColor}
            showHeader={false}
          />
          <span>Color</span>
        </InputDescriptionWrapper>
      </ControlsWrapper>
    </InlineInputCardWrapper>
  );
}

export interface BoxShadowValues {
  offsetX: string;
  offsetY: string;
  blurRadius: string;
  spreadRadius: string;
  color: string;
  inset: boolean;
}

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  gap: 1.5rem;

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  transition: opacity 0.2s ease-in-out;
`;
